/**
 * What the browser tab says.
 *
 * Every route rendered "AEGIS" until now, which makes the app unusable in the
 * one place it is most likely to be used: a second window, beside a trading
 * platform, in a strip of a dozen tabs. A trader who keeps the chart and the
 * journal open in two windows could not tell them apart.
 *
 * Section first, product second — `Journal · AEGIS`. A tab is truncated from
 * the right, so the half that identifies the tab has to come first; putting
 * the brand there gives every tab the identical visible prefix, which is the
 * problem rather than the fix.
 */

const SECTIONS: Record<string, string> = {
  '/': 'Chart',
  '/journal': 'Journal',
  '/analytics': 'Analytics',
  '/strategies': 'Strategies',
  '/reports': 'Reports',
  '/agents': 'Agents',
  '/backtest': 'Backtest',
  '/daily': 'Daily',
  '/alerts': 'Alerts',
  '/settings': 'Settings',
  '/more': 'More',
  '/today': 'Today',
  '/tax': 'Tax export',
  '/login': 'Sign in',
  '/signup': 'Create an account',
  '/pricing': 'Plans',
  '/forgot-password': 'Reset your password',
  '/reset-password': 'Choose a new password',
  '/verify-email': 'Verify your email',
  '/billing/success': 'Subscription active',
  '/billing/cancelled': 'Checkout cancelled',
};

export const BRAND = 'AEGIS';

/**
 * `path` is the unprefixed route — `/journal`, not `/app/journal`.
 *
 * Longest-prefix match, so `/settings/broker` is "Settings" rather than
 * falling through to the bare brand. An unknown route gets the brand alone:
 * a wrong section name is worse than none.
 */
export function titleFor(path: string, detail?: string | null): string {
  if (detail) return `${detail} · ${BRAND}`;

  const exact = SECTIONS[path];
  if (exact) return `${exact} · ${BRAND}`;

  let best = '';
  for (const key of Object.keys(SECTIONS)) {
    if (key !== '/' && path.startsWith(`${key}/`) && key.length > best.length) best = key;
  }
  return best ? `${SECTIONS[best]} · ${BRAND}` : BRAND;
}
